import { type StateTree, defineStore } from 'pinia';
import { useAuthStore } from '../use-auth-store';
import { type RecordEntityId } from '@centric-os/types';

export const menusPermissionScope = {
  readGlobalMenuGroupAdmin: 'read:global_menu_group:*',
  readAssignedGlobalMenuGroup: 'read:global_menu_group:',
  writeGlobalMenuGroupAdmin: 'write:global_menu_group:*',
  writeAssignedGlobalMenuGroup: 'write:global_menu_group:',
  writeNestedAssignedGlobalMenuGroup: 'write_nested:global_menu_group:',
  fullGlobalMenuGroupAdmin: '*:global_menu_group:*',
  fullLocalMenuGroupAdmin: '*:local_menu_group:*',
  writeLocalMenuGroupAdmin: 'write:local_menu_group:*',
  writeAssignedLocalMenuGroup: 'write:local_menu_group:',
  writeNestedAssignedLocalMenuGroup: 'write_nested:local_menu_group:',
  readLocalMenuGroupAdmin: 'read:local_menu_group:*',
  readAssignedLocalMenuGroup: 'read:local_menu_group:',
  writeNestedPublishLocalMenuGroupAdmin: 'write_nested_publish:local_menu_group:*',
  writeNestedPublishAssignedLocalMenuGroup: 'write_nested_publish:local_menu_group:',
  writeNestedIsActiveLocalMenuGroupAdmin: 'write_nested_is_active:local_menu_group:*',
  writeNestedIsActiveAssignedLocalMenuGroup: 'write_nested_is_active:local_menu_group:',
  writeLineRouteAssignedLocalMenuGroup: 'write_line_route:local_menu_group:',
};

interface State extends StateTree {
  globalMenuGroupId: RecordEntityId;
  localMenuGroupId: RecordEntityId;
}

export const useMenuPermissionsStore = defineStore('menuPermissions', {
  state: (): State => ({
    globalMenuGroupId: null,
    localMenuGroupId: null,
  }),
  getters: {
    canAccessGlobalMenuGroupsSection(): boolean {
      const authStore = useAuthStore();
      return (
        authStore.hasPermissions([menusPermissionScope.fullGlobalMenuGroupAdmin]) ||
        authStore.hasPermissions([menusPermissionScope.readGlobalMenuGroupAdmin]) ||
        authStore.hasPermissions([menusPermissionScope.readAssignedGlobalMenuGroup])
      );
    },
    canWriteGlobalMenuGroups(): boolean {
      const authStore = useAuthStore();
      return (
        authStore.hasPermissions([menusPermissionScope.writeGlobalMenuGroupAdmin]) ||
        authStore.hasPermissions([menusPermissionScope.fullGlobalMenuGroupAdmin])
      );
    },
    canWriteSubComponents(): boolean {
      if (this.globalMenuGroupId) {
        return this.canWriteGlobalSubComponents;
      } else if (this.localMenuGroupId) {
        return this.canWriteLocalSubComponents;
      } else {
        return false;
      }
    },
    canWriteGlobalSubComponents(): boolean {
      const authStore = useAuthStore();
      return (
        authStore.hasPermissions([menusPermissionScope.writeGlobalMenuGroupAdmin]) ||
        authStore.hasPermissions([menusPermissionScope.fullGlobalMenuGroupAdmin]) ||
        authStore.hasPermissions([
          `${menusPermissionScope.writeNestedAssignedGlobalMenuGroup}${this.globalMenuGroupId}`,
        ])
      );
    },
    canWriteAssignedGlobalMenuGroup(): boolean {
      const authStore = useAuthStore();
      return (
        authStore.hasPermissions([menusPermissionScope.writeGlobalMenuGroupAdmin]) ||
        authStore.hasPermissions([menusPermissionScope.fullGlobalMenuGroupAdmin]) ||
        authStore.hasPermissions([
          `${menusPermissionScope.writeAssignedGlobalMenuGroup}${this.globalMenuGroupId}`,
        ])
      );
    },
    canAccessLocalMenuGroupsSection(): boolean {
      const authStore = useAuthStore();
      return (
        authStore.hasPermissions([menusPermissionScope.readLocalMenuGroupAdmin]) ||
        authStore.hasPermissions([menusPermissionScope.readAssignedLocalMenuGroup]) ||
        authStore.hasPermissions([menusPermissionScope.fullLocalMenuGroupAdmin])
      );
    },
    canWriteLocalMenuGroups(): boolean {
      const authStore = useAuthStore();
      return (
        authStore.hasPermissions([menusPermissionScope.writeLocalMenuGroupAdmin]) ||
        authStore.hasPermissions([menusPermissionScope.fullLocalMenuGroupAdmin])
      );
    },
    canReadGlobalMenuGroup(): (globalMenuGroupId?: RecordEntityId) => boolean {
      const storeGlobalMenuGroupId = this.globalMenuGroupId;
      return (globalMenuGroupId: RecordEntityId = storeGlobalMenuGroupId): boolean => {
        const authStore = useAuthStore();
        return (
          authStore.hasPermissions([menusPermissionScope.readGlobalMenuGroupAdmin]) ||
          authStore.hasPermissions([menusPermissionScope.fullGlobalMenuGroupAdmin]) ||
          authStore.hasPermissions([
            `${menusPermissionScope.readAssignedGlobalMenuGroup}${globalMenuGroupId}`,
          ])
        );
      };
    },
    canWriteLocalMenuGroup(): (localMenuGroupId?: RecordEntityId) => boolean {
      const storeLocalMenuGroupId = this.localMenuGroupId;
      return (localMenuGroupId: RecordEntityId = storeLocalMenuGroupId): boolean => {
        const authStore = useAuthStore();
        return (
          authStore.hasPermissions([menusPermissionScope.writeLocalMenuGroupAdmin]) ||
          authStore.hasPermissions([menusPermissionScope.fullLocalMenuGroupAdmin]) ||
          authStore.hasPermissions([
            `${menusPermissionScope.writeAssignedLocalMenuGroup}${localMenuGroupId}`,
          ])
        );
      };
    },
    canWriteLocalSubComponents(): boolean {
      const authStore = useAuthStore();
      return (
        authStore.hasPermissions([menusPermissionScope.writeLocalMenuGroupAdmin]) ||
        authStore.hasPermissions([menusPermissionScope.fullLocalMenuGroupAdmin]) ||
        authStore.hasPermissions([
          `${menusPermissionScope.writeNestedAssignedLocalMenuGroup}${this.localMenuGroupId}`,
        ])
      );
    },
    canWriteLineRouteLocalMenuGroup(): boolean {
      const authStore = useAuthStore();
      return (
        this.canWriteLocalSubComponents ||
        authStore.hasPermissions([
          `${menusPermissionScope.writeLineRouteAssignedLocalMenuGroup}${this.localMenuGroupId}`,
        ])
      );
    },
    canPublishLmgBrand(): boolean {
      const authStore = useAuthStore();

      return (
        this.canWriteLocalSubComponents ||
        authStore.hasPermissions([
          `${menusPermissionScope.writeNestedPublishLocalMenuGroupAdmin}`,
        ]) ||
        authStore.hasPermissions([
          `${menusPermissionScope.writeNestedPublishAssignedLocalMenuGroup}${this.localMenuGroupId}`,
        ])
      );
    },
    canToggleLimitedEditNestedIsActive(): boolean {
      const authStore = useAuthStore();

      return (
        this.canWriteLocalSubComponents ||
        authStore.hasPermissions([
          `${menusPermissionScope.writeNestedIsActiveLocalMenuGroupAdmin}`,
        ]) ||
        authStore.hasPermissions([
          `${menusPermissionScope.writeNestedIsActiveAssignedLocalMenuGroup}${this.localMenuGroupId}`,
        ])
      );
    },
    isGlobalBrand: (state) => {
      return !!(state.globalMenuGroupId && !state.localMenuGroupId);
    },
  },
});
